import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import swal from 'sweetalert';
import { allCancelOrderByUserAction, clearErrorAction } from '../../redux/actions/orderAction';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import Loader from '../../layout/loader';
import MetaTag from '../../layout/MetaTag';
import EmptyOrder from './EmptyOrder';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';

import './cancelOrderDetail.css';

function CancelOrderDetail() {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);
  const { error, loading, cancelOrders } = useSelector((state) => state.allCancelOrderByUser);

  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'processing' | 'confirmed' | 'refunded'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (error) {
      swal({
        title: 'Error',
        text: error,
        icon: 'error',
      });
      dispatch(clearErrorAction());
    }

    dispatch(allCancelOrderByUserAction());
  }, [dispatch, error]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Pending Confirmation';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return String(dateString).substring(0, 10);
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return String(dateString).substring(0, 10);
    }
  };

  // Helper to categorize cancellation status
  const getStatusCategory = (statusStr) => {
    if (!statusStr) return 'processing';
    const s = statusStr.toLowerCase();
    if (s.includes('refund') || s.includes('complete')) {
      return 'refunded';
    }
    if (s.includes('cancel') || s.includes('approve') || s.includes('confirm')) {
      return 'confirmed';
    }
    if (s.includes('reject')) {
      return 'rejected';
    }
    return 'processing';
  };

  // Counts for tabs
  const tabCounts = useMemo(() => {
    const counts = { all: 0, processing: 0, confirmed: 0, refunded: 0 };
    if (!cancelOrders || !Array.isArray(cancelOrders)) return counts;

    counts.all = cancelOrders.length;
    cancelOrders.forEach((item) => {
      const cat = getStatusCategory(item.cancelInformation?.status);
      if (cat === 'processing') counts.processing++;
      else if (cat === 'confirmed') counts.confirmed++;
      else if (cat === 'refunded') counts.refunded++;
    });
    return counts;
  }, [cancelOrders]);

  // Filtered cancel orders
  const filteredOrders = useMemo(() => {
    if (!cancelOrders || !Array.isArray(cancelOrders)) return [];

    let list = cancelOrders;

    // Filter by tab
    if (activeTab !== 'all') {
      list = list.filter((item) => {
        const cat = getStatusCategory(item.cancelInformation?.status);
        return cat === activeTab;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const idMatch = String(item._id || '').toLowerCase().includes(q);
        const orderIdMatch = String(item.orderId || '').toLowerCase().includes(q);
        const reasonMatch = String(item.cancelReason || '').toLowerCase().includes(q);
        return idMatch || orderIdMatch || reasonMatch;
      });
    }

    return list;
  }, [cancelOrders, activeTab, searchQuery]);

  return (
    <Fragment>
      <MetaTag
        title={`Cancelled Orders & Refunds | IM Online Shopping`}
        description="Track your cancelled orders, cancellation confirmations, and refund settlement status on IM Online Shopping."
      />

      <div className="can-page-wrapper">
        <Navbar />

        <main className="can-container">
          {/* Breadcrumbs */}
          <nav className="can-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="can-breadcrumb-sep">/</span>
            <Link to="/userprofile">My Account</Link>
            <span className="can-breadcrumb-sep">/</span>
            <span className="can-breadcrumb-current">Cancelled Orders</span>
          </nav>

          {/* Hero Header */}
          <section className="can-hero">
            <div className="can-hero-left">
              <div className="can-hero-icon-box">
                <CancelOutlinedIcon />
              </div>
              <div className="can-hero-text">
                <h1>Cancelled Orders & Refunds</h1>
                <p>
                  Review cancellation details, approval confirmations, and refund settlement progress for {user?.firstName ? `${user.firstName}'s account` : 'your orders'}.
                </p>
                <div className="can-policy-chips">
                  <span className="can-chip">
                    <VerifiedUserOutlinedIcon /> 100% Refund Protection
                  </span>
                  <span className="can-chip">
                    <AccountBalanceOutlinedIcon /> Direct Payout to Source / Bank
                  </span>
                  <span className="can-chip">
                    <CheckCircleOutlineIcon /> Fast 24-48h Processing
                  </span>
                </div>
              </div>
            </div>

            <div className="can-hero-actions">
              <Link to="/orders/Details/me" className="can-btn-active">
                <ShoppingBagOutlinedIcon fontSize="small" />
                <span>View All Orders</span>
              </Link>
              <Link to="/userprofile" className="can-btn-back">
                <ArrowBackIosNewOutlinedIcon style={{ fontSize: '12px' }} />
                <span>Back to Profile</span>
              </Link>
            </div>
          </section>

          {loading ? (
            <Loader />
          ) : !cancelOrders || cancelOrders.length === 0 ? (
            <EmptyOrder />
          ) : (
            <Fragment>
              {/* Filter Tabs & Search */}
              <div className="can-filter-bar">
                <div className="can-status-tabs" role="tablist">
                  <button
                    type="button"
                    className={`can-tab-pill ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    <span>All Cancelled</span>
                    <span className="can-tab-badge">{tabCounts.all}</span>
                  </button>

                  <button
                    type="button"
                    className={`can-tab-pill ${activeTab === 'processing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('processing')}
                  >
                    <span>Processing</span>
                    <span className="can-tab-badge">{tabCounts.processing}</span>
                  </button>

                  <button
                    type="button"
                    className={`can-tab-pill ${activeTab === 'confirmed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('confirmed')}
                  >
                    <span>Confirmed</span>
                    <span className="can-tab-badge">{tabCounts.confirmed}</span>
                  </button>

                  <button
                    type="button"
                    className={`can-tab-pill ${activeTab === 'refunded' ? 'active' : ''}`}
                    onClick={() => setActiveTab('refunded')}
                  >
                    <span>Refund Settled</span>
                    <span className="can-tab-badge">{tabCounts.refunded}</span>
                  </button>
                </div>

                <div className="can-search-wrap">
                  <SearchIcon className="can-search-icon" />
                  <input
                    type="text"
                    className="can-search-input"
                    placeholder="Search by Cancel ID or Order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Cancel Orders List */}
              {filteredOrders.length === 0 ? (
                <div className="can-empty-card" style={{ padding: '36px 20px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '17px', color: '#111315', fontWeight: '800' }}>
                    No matching cancellations found
                  </h4>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
                    Try clearing your search query or selecting a different status filter tab.
                  </p>
                  <button
                    type="button"
                    className="can-btn-back"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveTab('all');
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              ) : (
                <div className="can-list">
                  {filteredOrders.map((item) => {
                    const statusStr = item.cancelInformation?.status || 'Processing';
                    const category = getStatusCategory(statusStr);
                    const formattedCancelId = String(item._id).slice(-8).toUpperCase();
                    const formattedOrderId = String(item.orderId).slice(-8).toUpperCase();
                    const createdAtFormatted = formatDate(item.cancelInformation?.createdAt);
                    const confirmDateFormatted = formatDate(item.cancelConfirmAt);

                    return (
                      <article key={item._id} className="can-card">
                        {/* Header */}
                        <div className="can-card-header">
                          <div className="can-card-meta-left">
                            <span className="can-id-badge" title={`Full Cancel ID: ${item._id}`}>
                              CAN #{formattedCancelId}
                            </span>
                            <Link
                              to={`/order/${item.orderId}`}
                              className="can-order-link"
                              title="Click to view original order details & invoice"
                            >
                              <span>Order #{formattedOrderId}</span>
                              <LaunchIcon />
                            </Link>
                            <span className="can-date-text">
                              Cancelled on {createdAtFormatted}
                            </span>
                          </div>

                          <div className={`can-status-badge ${category}`}>
                            {category === 'processing' && <AccessTimeOutlinedIcon style={{ fontSize: '14px' }} />}
                            {category === 'confirmed' && <CheckCircleOutlineIcon style={{ fontSize: '14px' }} />}
                            {category === 'refunded' && <AccountBalanceOutlinedIcon style={{ fontSize: '14px' }} />}
                            <span>{statusStr}</span>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="can-card-body">
                          <div className="can-info-row">
                            <span className="can-confirm-pill">
                              <CheckCircleOutlineIcon />
                              Confirmation: {confirmDateFormatted}
                            </span>

                            <span className="can-refund-indicator">
                              <AccountBalanceOutlinedIcon />
                              {category === 'refunded'
                                ? 'Refund Settled to Source Account'
                                : 'Refund Processing via IM Online'}
                            </span>
                          </div>

                          {/* Cancellation Reason */}
                          {item.cancelReason && (
                            <div className="can-reason-box">
                              <span className="can-reason-label">Reason for Cancellation</span>
                              <p className="can-reason-content">{item.cancelReason}</p>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="can-card-footer">
                          <div className="can-card-actions-left">
                            <Link to={`/order/${item.orderId}`} className="can-action-link primary">
                              <LaunchIcon style={{ fontSize: '14px' }} />
                              <span>View Order Invoice</span>
                            </Link>
                            <Link to="/user-profile/add-bank-account/" className="can-action-link">
                              <AccountBalanceOutlinedIcon style={{ fontSize: '14px' }} />
                              <span>Refund Bank Account</span>
                            </Link>
                          </div>

                          <Link to="/user/complain/request" className="can-action-link">
                            <SupportAgentOutlinedIcon style={{ fontSize: '15px' }} />
                            <span>Contact Support</span>
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {/* 3-Step Cancellation & Refund Process */}
              <section className="can-process-section" aria-label="How cancellation and refunds work">
                <h3 className="can-process-heading">
                  <VerifiedUserOutlinedIcon style={{ color: 'var(--can-brand)' }} />
                  <span>How IM Online Cancellations & Refunds Work</span>
                </h3>
                <div className="can-process-grid">
                  <div className="can-process-step">
                    <span className="can-step-num">1</span>
                    <h4 className="can-step-title">Cancel Order Request</h4>
                    <p className="can-step-desc">
                      Cancel any item before it is packed or dispatched directly from your order tracking screen.
                    </p>
                  </div>

                  <div className="can-process-step">
                    <span className="can-step-num">2</span>
                    <h4 className="can-step-title">Instant Verification</h4>
                    <p className="can-step-desc">
                      Our system and local merchant confirm the cancellation within 2-4 hours, stopping dispatch automatically.
                    </p>
                  </div>

                  <div className="can-process-step">
                    <span className="can-step-num">3</span>
                    <h4 className="can-step-title">Direct Payout</h4>
                    <p className="can-step-desc">
                      Prepaid orders are refunded back to your original payment mode (UPI/Card). Cash on Delivery orders are refunded directly to your registered bank account.
                    </p>
                  </div>
                </div>
              </section>
            </Fragment>
          )}
        </main>

        <Footer />
      </div>
    </Fragment>
  );
}

export default CancelOrderDetail;